import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authClient } from "../lib/auth-client";
import { LogoMark } from "./LogoMark";

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isPending } = authClient.useSession();

  const navItems = [
    { to: "/builder", label: "Builder" },
    { to: "/discover/templates", label: "Templates" },
    { to: "/pricing", label: "Pricing" },
  ];

  function isActive(path: string) {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  }

  async function handleSignOut() {
    setIsSigningOut(true);
    await authClient.signOut();
    setMobileOpen(false);
    navigate("/");
    setIsSigningOut(false);
  }

  const authLinks = isPending ? null : session ? (
    <>
      <Link
        to="/dashboard"
        className="text-sm font-medium text-slate-600 transition hover:text-slate-950"
      >
        Dashboard
      </Link>
      <button
        type="button"
        onClick={() => void handleSignOut()}
        disabled={isSigningOut}
        className="rounded-md border border-slate-200/80 bg-white/80 px-3.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-70"
      >
        {isSigningOut ? "Signing out..." : "Sign out"}
      </button>
    </>
  ) : (
    <>
      <Link
        to="/auth/login"
        className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100/70 hover:text-slate-950"
      >
        Sign in
      </Link>
      <Link
        to="/auth/signup?redirect=%2Fbuilder"
        className="group inline-flex items-center gap-1.5 rounded-md bg-slate-950 px-4 py-1.5 text-sm font-semibold text-white shadow-[0_8px_24px_-12px_rgba(15,23,42,0.6)] transition hover:bg-slate-800 hover:shadow-[0_12px_30px_-12px_rgba(15,23,42,0.7)]"
      >
        Get Started
        <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5">
          <path d="M4.166 10h11.667m0 0-4.167-4.167M15.833 10l-4.167 4.167" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    </>
  );

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-slate-200/60 bg-white/75 backdrop-blur-xl supports-[backdrop-filter]:bg-white/65">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:h-15 sm:px-6 lg:px-8">
        <Link to="/" className="group inline-flex items-center gap-2.5 text-[15px] font-semibold tracking-tight text-slate-950 transition">
          <span className="relative inline-flex items-center justify-center">
            <span aria-hidden="true" className="absolute inset-0 rounded-md bg-sky-400/0 blur-md transition-all duration-300 group-hover:bg-sky-400/40" />
            <LogoMark className="relative h-7 w-7 transition-transform duration-300 group-hover:scale-[1.06]" />
          </span>
          <span className="transition group-hover:text-sky-700">React Email Builder</span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => {
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={active ? "page" : undefined}
                className={`relative rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  active
                    ? "text-slate-950"
                    : "text-slate-600 hover:bg-slate-100/70 hover:text-slate-950"
                }`}
              >
                {item.label}
                {active ? (
                  <span aria-hidden="true" className="absolute inset-x-3 -bottom-px h-px bg-gradient-to-r from-transparent via-sky-500 to-transparent" />
                ) : null}
              </Link>
            );
          })}
          <div className="relative ml-1 group/dropdown">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100/70 hover:text-slate-950"
            >
              Resources
              <svg aria-hidden="true" className="h-4 w-4 transition-transform duration-150 group-hover/dropdown:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {/* Hover bridge: invisible padding fills the gap between button and dropdown */}
            <div className="absolute left-0 top-full h-3 w-full" aria-hidden="true" />
            <div className="absolute left-0 top-full z-50 hidden w-60 overflow-hidden rounded-md border border-slate-200/70 bg-white/95 py-1.5 shadow-card ring-1 ring-black/5 backdrop-blur-xl group-hover/dropdown:block">
              <Link
                to="/blog"
                className="block px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-50"
              >
                All posts
              </Link>
              <Link
                to="/blog/best-react-email-templates"
                className="block px-4 py-2.5 text-sm text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
              >
                Best React Email Templates
              </Link>
              <Link
                to="/blog/build-email-templates-with-ai"
                className="block px-4 py-2.5 text-sm text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
              >
                Build with AI
              </Link>
              <Link
                to="/blog/react-email-vs-mjml"
                className="block px-4 py-2.5 text-sm text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
              >
                React Email vs MJML
              </Link>
              <hr className="my-2 border-slate-100" />
              <Link
                to="/about"
                className="block px-4 py-2.5 text-sm text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
              >
                About
              </Link>
            </div>
          </div>
        </div>

        {/* Desktop auth */}
        <div className="hidden items-center gap-2 md:flex">
          {authLinks}
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md p-2 text-slate-600 transition hover:text-slate-950 md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <svg aria-hidden="true" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg aria-hidden="true" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-slate-100 bg-white px-4 pb-4 pt-2 md:hidden">
          <div className="flex flex-col gap-3">
            <Link
              to="/builder"
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
            >
              Builder
            </Link>
            <Link
              to="/discover/templates"
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
            >
              Templates
            </Link>
            <Link
              to="/pricing"
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
            >
              Pricing
            </Link>
            <div className="pl-2">
              <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Resources</p>
              <Link
                to="/blog"
                onClick={() => setMobileOpen(false)}
                className="block rounded-md px-3 py-2 text-sm font-semibold text-slate-950 transition hover:bg-slate-50"
              >
                All posts
              </Link>
              <Link
                to="/blog/best-react-email-templates"
                onClick={() => setMobileOpen(false)}
                className="block rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
              >
                Best React Email Templates
              </Link>
              <Link
                to="/blog/build-email-templates-with-ai"
                onClick={() => setMobileOpen(false)}
                className="block rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
              >
                Build Templates with AI
              </Link>
              <Link
                to="/blog/react-email-vs-mjml"
                onClick={() => setMobileOpen(false)}
                className="block rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
              >
                React Email vs MJML
              </Link>
              <hr className="my-1 border-slate-100" />
              <Link
                to="/about"
                onClick={() => setMobileOpen(false)}
                className="block rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
              >
                About
              </Link>
            </div>
            <hr className="my-1 border-slate-100" />
            {isPending ? null : session ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
                >
                  Dashboard
                </Link>
                <button
                  type="button"
                  onClick={() => void handleSignOut()}
                  disabled={isSigningOut}
                  className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSigningOut ? "Signing out..." : "Sign out"}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/auth/login"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
                >
                  Sign in
                </Link>
                <Link
                  to="/auth/signup?redirect=%2Fbuilder"
                  onClick={() => setMobileOpen(false)}
                  className="rounded bg-slate-950 px-4 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}